import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ChevronDown, ExternalLink, Globe2, Lightbulb, Network, Sparkles, Video, X } from "lucide-react";
import type { DetailFact, DetailType, EntityDetail } from "#shared/schemas/entityDetail.ts";
import type { FieldDef } from "#shared/schemas/fieldDef.ts";
import type { IdeaEntityType } from "#shared/schemas/idea.ts";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { useFieldDefs } from "@web/features/library/api.ts";
import { useEnsureIdea, useCopyEntityResearchPrompt } from "@web/features/ideas/ideasApi.ts";
import { cn } from "@web/lib/utils.ts";
import { useDetailStore, type DetailTarget } from "@web/stores/detail.ts";
import { useBrowseTrailStore, type ArtistSheetFocus } from "@web/stores/browseTrail.ts";
import { useGraphPathStore } from "@web/stores/graphPath.ts";
import { useGlobeStore } from "@web/stores/globe.ts";
import { useGlobeShotStore } from "@web/stores/globeShot.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { toast } from "sonner";
import { useEntityDetail, useSaveFieldValues, useSaveGenres, useSaveStatus, useSaveTrackBpm, useAdoptGenre, useDismissGenre } from "./api.ts";
import { CountryChipRow } from "./CountryChipRow.tsx";
import { FieldEditor } from "./FieldEditor.tsx";
import { GenreChipRow } from "./GenreChipRow.tsx";
import { StatusSection } from "./StatusSection.tsx";
import { CollaboratorsSection } from "./CollaboratorsSection.tsx";
import { ArtistTracksSection } from "./ArtistTracksSection.tsx";
import { ArtistAlbumsSection } from "./ArtistAlbumsSection.tsx";
import { ArtistPlaylistsSection } from "./ArtistPlaylistsSection.tsx";
import { AlbumTracksSection } from "./AlbumTracksSection.tsx";
import { PlaylistEnrichSection } from "./PlaylistEnrichSection.tsx";
import { PlaylistPublishSection } from "./PlaylistPublishSection.tsx";
import { MetaSection } from "./MetaSection.tsx";
import { TrackPlaylistsSection } from "./TrackPlaylistsSection.tsx";
import { SheetCopyButton } from "./SheetCopyButton.tsx";
import { SheetSyncButton } from "./SheetSyncButton.tsx";
import { useOpenDetail } from "./useOpenDetail.ts";
import { formatFact, presentFacts } from "./facts.ts";
import { pickHeroFacts } from "./heroFacts.ts";
import { BpmBadge } from "./BpmBadge.tsx";

/** One-map graph focuses artists only — jump to the entity or its first credited artist. */
function graphFocusArtist(detail: EntityDetail): { id: string; label: string } | null {
  if (detail.entity_type === "artist") {
    return { id: detail.entity_id, label: detail.title };
  }
  if (detail.entity_type !== "track" && detail.entity_type !== "album") return null;
  const artistFact = detail.facts.find((f) => f.link?.type === "artist");
  if (!artistFact?.link) return null;
  const label =
    typeof artistFact.value === "string"
      ? artistFact.value.split(",")[0]?.trim() || detail.title
      : detail.subtitle?.split(",")[0]?.trim() || "Artist";
  return { id: artistFact.link.id, label };
}

function factString(detail: EntityDetail, label: string): string | null {
  const value = detail.facts.find((f) => f.label === label)?.value;
  return typeof value === "string" ? value : null;
}

const TYPE_LABEL: Record<DetailType, string> = {
  track: "Track",
  album: "Album",
  artist: "Artist",
  playlist: "Spotify playlist",
  folder: "Folder",
  local_playlist: "Playlist",
  genre: "Genre",
};

type TabId = "overview" | "notes" | "meta";

const TAB_LABEL: Record<TabId, string> = {
  overview: "Overview",
  notes: "Notes",
  meta: "Meta",
};

function TabStrip({
  tabs,
  active,
  onChange,
}: {
  tabs: readonly TabId[];
  active: TabId;
  onChange: (tab: TabId) => void;
}) {
  const refs = useRef(new Map<TabId, HTMLButtonElement>());

  function step(delta: number) {
    const index = tabs.indexOf(active);
    const next = tabs[(index + delta + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next);
    refs.current.get(next)?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label="Detail sections"
      className="flex gap-1 overflow-x-auto border-b border-line px-2"
    >
      {tabs.map((tab) => (
        <button
          key={tab}
          ref={(el) => {
            if (el) refs.current.set(tab, el);
            else refs.current.delete(tab);
          }}
          role="tab"
          type="button"
          id={`detail-tab-${tab}`}
          aria-selected={tab === active}
          aria-controls={`detail-panel-${tab}`}
          tabIndex={tab === active ? 0 : -1}
          className={cn(
            "-mb-px border-b-2 px-2 py-2 text-xs font-semibold",
            tab === active
              ? "border-accent text-text"
              : "border-transparent text-muted hover:text-text",
          )}
          onClick={() => onChange(tab)}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") {
              e.preventDefault();
              step(1);
            }
            if (e.key === "ArrowLeft") {
              e.preventDefault();
              step(-1);
            }
          }}
        >
          {TAB_LABEL[tab]}
        </button>
      ))}
    </div>
  );
}

function FactRow({ fact, onOpen }: { fact: DetailFact; onOpen: (target: DetailTarget) => void }) {
  const text = formatFact(fact);

  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="shrink-0 text-xs text-faint">{fact.label}</span>
      {fact.link ? (
        <button
          type="button"
          className="min-w-0 truncate text-right text-sm text-accent hover:underline"
          onClick={() => {
            const link = fact.link;
            if (link) onOpen({ type: link.type, id: link.id });
          }}
        >
          {text}
        </button>
      ) : fact.kind === "url" ? (
        <a
          href={text}
          target="_blank"
          rel="noreferrer"
          className="min-w-0 truncate text-right text-sm text-accent hover:underline"
        >
          {text}
        </a>
      ) : (
        <span className="min-w-0 truncate text-right text-sm text-text">{text}</span>
      )}
    </div>
  );
}

interface SaveEntry {
  status: "idle" | "pending" | "error" | "success";
  submittedAt: number;
  error: Error | null;
  retry: () => void;
}

function SaveState({ entries }: { entries: readonly SaveEntry[] }) {
  const pending = entries.some((e) => e.status === "pending");
  const latest = [...entries]
    .filter((e) => e.status === "error" || e.status === "success")
    .sort((a, b) => b.submittedAt - a.submittedAt)[0];

  return (
    <div aria-live="polite" className="flex h-5 items-center gap-2 text-xs">
      {pending ? (
        <span className="text-muted">Saving…</span>
      ) : latest?.status === "error" ? (
        <>
          <span className="text-status-cancelled">
            {latest.error?.message ?? "Save failed"}
          </span>
          <button
            type="button"
            className="font-semibold text-accent hover:underline"
            onClick={latest.retry}
          >
            Retry
          </button>
        </>
      ) : latest?.status === "success" ? (
        <span className="text-faint">Saved</span>
      ) : null}
    </div>
  );
}

function RelatedSection({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="rounded-md border border-line">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="text-xs font-semibold tracking-wider text-faint uppercase">{title}</span>
        <ChevronDown
          className={cn("size-4 text-faint transition-transform", open && "rotate-180")}
        />
      </button>
      {open && <div className="border-t border-line px-2 pb-2">{children}</div>}
    </section>
  );
}

function EntitySheetBody({
  target,
  onClose,
}: {
  target: DetailTarget;
  onClose: () => void;
}) {
  const openDetail = useOpenDetail();
  const navigate = useNavigate();
  const focusCollaborators = useBrowseTrailStore((s) => s.focusCollaborators);
  const artistSheetFocus = useBrowseTrailStore((s) => s.artistSheetFocus);
  const sheetSectionFocus = useBrowseTrailStore((s) => s.sheetSectionFocus);
  const setArtistSheetFocus = useBrowseTrailStore((s) => s.setArtistSheetFocus);
  const setSheetSectionFocus = useBrowseTrailStore((s) => s.setSheetSectionFocus);
  const setFocusCollaborators = useBrowseTrailStore((s) => s.setFocusCollaborators);
  const resetGraph = useGraphPathStore((s) => s.resetTo);
  const setArtistsView = useUiStore((s) => s.setArtistsView);
  const showMetaTab = useUiStore((s) => s.showMetaTab);
  const detailQuery = useEntityDetail(target);
  const fieldDefs = useFieldDefs();

  const saveValues = useSaveFieldValues(target);
  const saveStatus = useSaveStatus(target);
  const saveBpm = useSaveTrackBpm(target);
  const saveGenres = useSaveGenres(target);
  const adoptGenre = useAdoptGenre(target);
  const dismissGenre = useDismissGenre(target);
  const ensureIdea = useEnsureIdea();
  const copyResearch = useCopyEntityResearchPrompt();

  const detail = detailQuery.data;

  useEffect(() => {
    if (!detail) return;
    const top = useBrowseTrailStore.getState().stack.at(-1);
    if (
      top &&
      top.type === target.type &&
      top.id === target.id &&
      top.label !== detail.title
    ) {
      useBrowseTrailStore.getState().relabelTop(detail.title);
    }
  }, [detail, target.type, target.id]);

  const tabs = useMemo<TabId[]>(() => {
    if (!detail) return ["overview"];
    const list: TabId[] = ["overview"];
    if (detail.supports.fields) list.push("notes");
    if (
      showMetaTab &&
      (detail.entity_type === "track" ||
        detail.entity_type === "album" ||
        detail.entity_type === "artist")
    ) {
      list.push("meta");
    }
    return list;
  }, [detail, showMetaTab]);

  const [tab, setTab] = useState<TabId>("overview");
  const [moreOpen, setMoreOpen] = useState(false);
  const [samplesOpen, setSamplesOpen] = useState(false);
  const [relatedOpen, setRelatedOpen] = useState<Record<ArtistSheetFocus, boolean>>({
    tracks: false,
    albums: false,
    playlists: false,
    collabs: false,
  });

  useEffect(() => {
    if (!tabs.includes(tab)) setTab("overview");
  }, [tabs, tab]);

  useEffect(() => {
    const focus = artistSheetFocus ?? (focusCollaborators ? "collabs" : null);
    if (!focus) return;
    setTab("overview");
    setRelatedOpen((prev) => ({ ...prev, [focus]: true }));
    setArtistSheetFocus(null);
    setFocusCollaborators(false);
    requestAnimationFrame(() => {
      document.getElementById(`sheet-related-${focus}`)?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  }, [
    artistSheetFocus,
    focusCollaborators,
    setArtistSheetFocus,
    setFocusCollaborators,
  ]);

  useEffect(() => {
    if (sheetSectionFocus !== "samples") return;
    setTab("overview");
    setSamplesOpen(true);
    setSheetSectionFocus(null);
    requestAnimationFrame(() => {
      document.getElementById("sheet-related-samples")?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  }, [sheetSectionFocus, setSheetSectionFocus]);

  const applicableDefs = useMemo(() => {
    if (!detail) return [];
    return (fieldDefs.data ?? []).filter((def) =>
      def.entity_types.some((entity) => entity === detail.entity_type),
    );
  }, [fieldDefs.data, detail]);

  const overviewDefs = useMemo(
    () => applicableDefs.filter((d) => d.group_name === "Overview" && d.type !== "longtext"),
    [applicableDefs],
  );

  /** Country chips handle artists; keep Country field editor only for other types. */
  const topOverviewDefs = useMemo(
    () =>
      overviewDefs.filter(
        (d) =>
          d.type === "country" && detail?.entity_type !== "artist",
      ),
    [overviewDefs, detail?.entity_type],
  );

  const belowStatusOverviewDefs = useMemo(
    () => overviewDefs.filter((d) => d.type !== "country"),
    [overviewDefs],
  );

  const moreFieldGroups = useMemo(() => {
    const order: string[] = [];
    const bag = new Map<string, FieldDef[]>();
    for (const def of applicableDefs) {
      if (def.type === "longtext") continue;
      if (def.group_name === "Overview") continue;
      if (def.group_name === "Texts") continue;
      const name = def.group_name ?? "Fields";
      if (!bag.has(name)) {
        bag.set(name, []);
        order.push(name);
      }
      bag.get(name)?.push(def);
    }
    order.sort((a, b) => a.localeCompare(b));
    return order.map((name) => ({ name, defs: bag.get(name) ?? [] }));
  }, [applicableDefs]);

  const notesDefs = useMemo(() => {
    const longtext = applicableDefs.filter((d) => d.type === "longtext");
    const textsGroup = applicableDefs.filter(
      (d) => d.group_name === "Texts" && d.type !== "longtext",
    );
    return [...longtext, ...textsGroup];
  }, [applicableDefs]);

  const { hero, rest } = useMemo(
    () => (detail ? pickHeroFacts(detail) : { hero: [], rest: [] }),
    [detail],
  );

  const saveEntries: SaveEntry[] = [
    {
      status: saveValues.status,
      submittedAt: saveValues.submittedAt,
      error: saveValues.error,
      retry: () => {
        const vars = saveValues.variables;
        if (vars) saveValues.mutate(vars);
      },
    },
    {
      status: saveStatus.status,
      submittedAt: saveStatus.submittedAt,
      error: saveStatus.error,
      retry: () => {
        const vars = saveStatus.variables;
        if (vars) saveStatus.mutate(vars);
      },
    },
    {
      status: saveGenres.status,
      submittedAt: saveGenres.submittedAt,
      error: saveGenres.error,
      retry: () => {
        const vars = saveGenres.variables;
        if (vars) saveGenres.mutate(vars);
      },
    },
    {
      status: saveBpm.status,
      submittedAt: saveBpm.submittedAt,
      error: saveBpm.error,
      retry: () => {
        const vars = saveBpm.variables;
        if (vars) saveBpm.mutate(vars);
      },
    },
  ];

  const busy = saveEntries.some((entry) => entry.status === "pending");

  const canExploreArtistGraph =
    detail &&
    (detail.entity_type === "artist" ||
      detail.entity_type === "album" ||
      detail.entity_type === "track");

  const canExplorePlaylistGraph =
    detail &&
    (detail.entity_type === "playlist" || detail.entity_type === "local_playlist");

  const canExploreGraph = canExploreArtistGraph || canExplorePlaylistGraph;

  const canFocusOnGlobe =
    detail &&
    (detail.entity_type === "artist" ||
      detail.entity_type === "album" ||
      detail.entity_type === "track");

  const ideaEntityType: IdeaEntityType | null =
    detail?.entity_type === "track" ||
    detail?.entity_type === "album" ||
    detail?.entity_type === "artist" ||
    detail?.entity_type === "playlist" ||
    detail?.entity_type === "local_playlist"
      ? detail.entity_type
      : null;

  function exploreInGraph() {
    if (!detail || !canExploreGraph) return;
    if (detail.entity_type === "playlist") {
      void navigate(`/playlist/${encodeURIComponent(detail.entity_id)}/explore`);
      return;
    }
    if (detail.entity_type === "local_playlist") {
      void navigate(`/local/${encodeURIComponent(detail.entity_id)}/explore`);
      return;
    }
    if (!canExploreArtistGraph) return;
    const focus = graphFocusArtist(detail);
    if (!focus) {
      toast.message("No artist to focus", {
        description: "This item has no credited artists in your library yet.",
      });
      return;
    }
    resetGraph({ type: "artist", id: focus.id, label: focus.label });
    setArtistsView("graph");
    void navigate("/artists");
  }

  function focusOnGlobe() {
    if (!detail || !canFocusOnGlobe) return;
    const focus = graphFocusArtist(detail);
    if (!focus) {
      toast.message("No artist to focus", {
        description: "This item has no credited artists in your library yet.",
      });
      return;
    }
    useGlobeStore.getState().requestFocus({ kind: "artist", id: focus.id });
    void navigate("/globe");
  }

  function recordShotOnGlobe() {
    if (!detail || !canFocusOnGlobe) return;
    const focus = graphFocusArtist(detail);
    if (!focus) {
      toast.message("No artist to record", {
        description: "This item has no credited artists in your library yet.",
      });
      return;
    }
    useGlobeShotStore.getState().requestRecord({ kind: "artist", id: focus.id });
    void navigate("/globe");
  }

  const coverUrl = detail?.image_url ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="shrink-0 border-b border-line">
        {coverUrl ? (
          <div className="relative aspect-sheet-hero w-full overflow-hidden bg-panel">
            <img
              src={coverUrl}
              alt=""
              decoding="async"
              className="size-full object-cover"
            />
            <div
              className="pointer-events-none absolute inset-0 shadow-inset-cover"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-sheet-hero-fade"
              aria-hidden
            />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close detail sheet"
              onClick={onClose}
              className="absolute top-2 right-2 z-20 rounded-full bg-bg/60 text-text hover:bg-bg/85 hover:text-text"
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : null}

        <div className="flex items-start gap-3 p-4 pb-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs tracking-wider text-faint uppercase">
              {TYPE_LABEL[detail?.entity_type ?? target.type]}
            </p>
            <h2
              className="line-clamp-2 text-lg font-bold text-pretty"
              title={detail?.title}
            >
              {detail?.title ?? "Loading…"}
            </h2>
            {detail?.subtitle_links && detail.subtitle_links.length > 0 ? (
              <p className="mt-0.5 line-clamp-2 text-sm text-muted">
                {detail.subtitle_links.map((link, i) => (
                  <span key={`${link.type}:${link.id}`}>
                    {i > 0 && ", "}
                    <button
                      type="button"
                      className="max-w-full truncate text-left hover:text-text hover:underline"
                      title={link.label}
                      onClick={() =>
                        openDetail(
                          { type: link.type, id: link.id, label: link.label },
                          { mode: "push" },
                        )
                      }
                    >
                      {link.label}
                    </button>
                  </span>
                ))}
              </p>
            ) : detail?.subtitle ? (
              <p className="mt-0.5 line-clamp-2 text-sm text-muted">{detail.subtitle}</p>
            ) : null}
            {detail?.entity_type === "track" && (
              <div className="mt-2">
                <BpmBadge
                  bpm={detail.bpm}
                  manual={detail.bpm_manual}
                  disabled={busy}
                  onCommit={(next) => saveBpm.mutate({ bpm: next })}
                />
              </div>
            )}
          </div>
          {!coverUrl && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close detail sheet"
              onClick={onClose}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>

        {detail && (
          <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
            <SheetCopyButton detail={detail} defs={applicableDefs} />
            <SheetSyncButton entityType={detail.entity_type} entityId={detail.entity_id} />
            {(detail.entity_type === "track" ||
              detail.entity_type === "album" ||
              detail.entity_type === "artist") && (
              <button
                type="button"
                aria-label="Research prompt"
                title="Research prompt"
                disabled={copyResearch.isPending}
                onClick={() => {
                  const t = detail.entity_type;
                  if (t === "track" || t === "album" || t === "artist") {
                    copyResearch.mutate({
                      entity_type: t,
                      entity_id: detail.entity_id,
                    });
                  }
                }}
                className={cn(
                  "inline-flex size-7 shrink-0 items-center justify-center rounded-full border transition-colors",
                  "border-line bg-panel text-muted hover:border-research hover:text-research",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60",
                  "disabled:opacity-50",
                )}
              >
                <Sparkles className="size-3.5" aria-hidden />
              </button>
            )}
            {detail.wikipedia_url && (
              <a
                href={detail.wikipedia_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
              >
                Wikipedia <ExternalLink className="size-3" />
              </a>
            )}
            {detail.external_url && (
              <a
                href={detail.external_url}
                target="_blank"
                rel="noreferrer"
                aria-label="Open in Spotify"
                title="Open in Spotify"
                className={cn(
                  "inline-flex size-7 shrink-0 items-center justify-center rounded-full border transition-colors",
                  "border-line bg-panel text-muted hover:border-accent hover:text-accent",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60",
                )}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="size-3.5 fill-current"
                  aria-hidden
                >
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.092-.779-.258-.87-.699-.092-.429.258-.779.699-.87 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.313 1.15zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                </svg>
              </a>
            )}
            {ideaEntityType && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted hover:text-text disabled:opacity-50"
                disabled={ensureIdea.isPending}
                onClick={() =>
                  ensureIdea.mutate({
                    entity_type: ideaEntityType,
                    entity_id: detail.entity_id,
                    title: detail.title,
                  })
                }
              >
                <Lightbulb className="size-3" />
                Start idea
              </button>
            )}
            {canExploreGraph && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted hover:text-text"
                onClick={exploreInGraph}
              >
                <Network className="size-3" />
                Explore in graph
              </button>
            )}
            {canFocusOnGlobe && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted hover:text-text"
                onClick={focusOnGlobe}
              >
                <Globe2 className="size-3" />
                Focus on globe
              </button>
            )}
            {canFocusOnGlobe && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted hover:text-text"
                onClick={recordShotOnGlobe}
              >
                <Video className="size-3" />
                Record shot
              </button>
            )}
          </div>
        )}
      </header>

      {detailQuery.isPending && (
        <div className="space-y-2 p-4">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      )}

      {detailQuery.isError && (
        <div className="p-4">
          <EmptyState
            title="Could not load this entity"
            description={detailQuery.error.message}
            action={
              <Button variant="secondary" size="sm" onClick={() => void detailQuery.refetch()}>
                Try again
              </Button>
            }
          />
        </div>
      )}

      {detail && (
        <>
          <TabStrip tabs={tabs} active={tab} onChange={setTab} />

          <div className="px-4 pt-2">
            <SaveState entries={saveEntries} />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-1 pb-6">
            <div
              role="tabpanel"
              id={`detail-panel-${tab}`}
              aria-labelledby={`detail-tab-${tab}`}
              tabIndex={-1}
            >
              {tab === "overview" && (
                <div className="space-y-5">
                  {(detail.supports.genres ||
                    detail.genres_computed ||
                    detail.genres.length > 0 ||
                    (detail.suggested_genres?.length ?? 0) > 0) && (
                    <GenreChipRow
                      detail={detail}
                      disabled={busy}
                      onAssign={(genre) =>
                        saveGenres.mutate({
                          add: [genre.genre_id],
                          preview: [genre],
                        })
                      }
                      onRemove={(genreId) => saveGenres.mutate({ remove: [genreId] })}
                      onAdopt={(s) => adoptGenre.mutate(s)}
                      onDismiss={(s, scope) =>
                        dismissGenre.mutate({ suggestion: s, scope })
                      }
                    />
                  )}

                  {detail.entity_type === "artist" && (
                    <CountryChipRow
                      detail={detail}
                      disabled={busy}
                      onSet={(code) => saveValues.mutate({ country: code })}
                      onClear={() => saveValues.mutate({ country: null })}
                    />
                  )}

                  {topOverviewDefs.map((def) => (
                    <label key={def.id} className="block space-y-1">
                      <span className="text-xs font-semibold tracking-wider text-faint uppercase">
                        {def.label}
                      </span>
                      <FieldEditor
                        def={def}
                        value={detail.values[def.key]}
                        disabled={busy}
                        onCommit={(next) => saveValues.mutate({ [def.key]: next })}
                      />
                    </label>
                  ))}

                  {hero.length > 0 && (
                    <section className="space-y-1">
                      <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">
                        Facts
                      </h3>
                      <div className="divide-y divide-line">
                        {hero.map((fact) => (
                          <FactRow
                            key={fact.label}
                            fact={fact}
                            onOpen={(t) =>
                              openDetail(
                                { ...t, label: formatFact(fact) },
                                { mode: "push" },
                              )
                            }
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {detail.supports.status && (
                    <StatusSection
                      detail={detail}
                      disabled={busy}
                      onSave={(patch) => saveStatus.mutate(patch)}
                    />
                  )}

                  {detail.entity_type === "local_playlist" && (
                    <PlaylistPublishSection
                      localPlaylistId={detail.entity_id}
                      spotifyUrl={detail.external_url}
                      publishedAt={factString(detail, "Last created on Spotify")}
                    />
                  )}

                  {(detail.entity_type === "playlist" ||
                    detail.entity_type === "local_playlist") && (
                    <section className="space-y-2 rounded-md border border-line bg-panel px-3 py-3">
                      <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">
                        Fetch data
                      </h3>
                      <PlaylistEnrichSection
                        playlistId={detail.entity_type === "playlist" ? detail.entity_id : null}
                        localPlaylistId={
                          detail.entity_type === "local_playlist" ? detail.entity_id : null
                        }
                      />
                    </section>
                  )}

                  {(detail.sample_relations?.length ?? 0) > 0 && (
                    <section id="sheet-related-samples" className="rounded-md border border-line">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
                        aria-expanded={samplesOpen}
                        onClick={() => setSamplesOpen((v) => !v)}
                      >
                        <span className="text-xs font-semibold tracking-wider text-faint uppercase">
                          Samples · {detail.sample_relations.length}
                        </span>
                        <ChevronDown
                          className={cn(
                            "size-4 text-faint transition-transform",
                            samplesOpen && "rotate-180",
                          )}
                        />
                      </button>
                      {samplesOpen && (
                        <ul className="divide-y divide-line border-t border-line">
                          {detail.sample_relations.map((rel, i) => (
                            <li key={`${rel.type}-${rel.name}-${i}`} className="px-3 py-2">
                              <div className="text-xs text-faint">{rel.type}</div>
                              {rel.url ? (
                                <a
                                  href={rel.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm text-accent hover:underline"
                                >
                                  {rel.name}
                                </a>
                              ) : (
                                <div className="text-sm text-text">{rel.name}</div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )}

                  {belowStatusOverviewDefs.map((def) => (
                    <label key={def.id} className="block space-y-1">
                      <span className="text-xs text-muted">{def.label}</span>
                      <FieldEditor
                        def={def}
                        value={detail.values[def.key]}
                        disabled={busy}
                        onCommit={(next) => saveValues.mutate({ [def.key]: next })}
                      />
                    </label>
                  ))}

                  {(rest.length > 0 ||
                    moreFieldGroups.length > 0 ||
                    detail.providers.length > 0) && (
                    <section className="rounded-md border border-line">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
                        aria-expanded={moreOpen}
                        onClick={() => setMoreOpen((v) => !v)}
                      >
                        <span className="text-xs font-semibold tracking-wider text-faint uppercase">
                          More
                        </span>
                        <ChevronDown
                          className={cn(
                            "size-4 text-faint transition-transform",
                            moreOpen && "rotate-180",
                          )}
                        />
                      </button>
                      {moreOpen && (
                        <div className="space-y-5 border-t border-line px-3 py-3">
                          {rest.length > 0 && (
                            <div className="divide-y divide-line">
                              {rest.map((fact) => (
                                <FactRow
                                  key={fact.label}
                                  fact={fact}
                                  onOpen={(t) =>
                                    openDetail(
                                      { ...t, label: formatFact(fact) },
                                      { mode: "push" },
                                    )
                                  }
                                />
                              ))}
                            </div>
                          )}

                          {moreFieldGroups.map((group) => (
                            <div key={group.name} className="space-y-3">
                              <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">
                                {group.name}
                              </h3>
                              {group.defs.map((def) => (
                                <label key={def.id} className="block space-y-1">
                                  <span className="text-xs text-muted">{def.label}</span>
                                  <FieldEditor
                                    def={def}
                                    value={detail.values[def.key]}
                                    disabled={busy}
                                    onCommit={(next) => saveValues.mutate({ [def.key]: next })}
                                  />
                                </label>
                              ))}
                            </div>
                          ))}

                          {detail.providers.map((block) => (
                            <div key={`${block.label}-${block.source}`} className="space-y-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">
                                  {block.label}
                                </h3>
                                <span className="text-xs text-faint">{block.source}</span>
                              </div>
                              <div className="divide-y divide-line">
                                {presentFacts(block.facts).map((fact) => (
                                  <FactRow
                                    key={fact.label}
                                    fact={fact}
                                    onOpen={(t) =>
                                      openDetail(
                                        { ...t, label: formatFact(fact) },
                                        { mode: "push" },
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}

                  {detail.entity_type === "track" && (
                    <div className="space-y-2">
                      <RelatedSection
                        id="sheet-related-track-playlists"
                        title="Playlists"
                        open={relatedOpen.playlists}
                        onToggle={() =>
                          setRelatedOpen((p) => ({ ...p, playlists: !p.playlists }))
                        }
                      >
                        <TrackPlaylistsSection trackId={detail.entity_id} />
                      </RelatedSection>
                    </div>
                  )}

                  {detail.entity_type === "album" && (
                    <div className="space-y-2">
                      <RelatedSection
                        id="sheet-related-album-tracks"
                        title="Tracks in library"
                        open={relatedOpen.tracks}
                        onToggle={() =>
                          setRelatedOpen((p) => ({ ...p, tracks: !p.tracks }))
                        }
                      >
                        <AlbumTracksSection albumId={detail.entity_id} />
                      </RelatedSection>
                    </div>
                  )}

                  {detail.entity_type === "artist" && (
                    <div className="space-y-2">
                      <RelatedSection
                        id="sheet-related-tracks"
                        title="Tracks in library"
                        open={relatedOpen.tracks}
                        onToggle={() =>
                          setRelatedOpen((p) => ({ ...p, tracks: !p.tracks }))
                        }
                      >
                        <ArtistTracksSection artistId={detail.entity_id} />
                      </RelatedSection>
                      <RelatedSection
                        id="sheet-related-albums"
                        title="Albums in library"
                        open={relatedOpen.albums}
                        onToggle={() =>
                          setRelatedOpen((p) => ({ ...p, albums: !p.albums }))
                        }
                      >
                        <ArtistAlbumsSection artistId={detail.entity_id} />
                      </RelatedSection>
                      <RelatedSection
                        id="sheet-related-playlists"
                        title="Playlists"
                        open={relatedOpen.playlists}
                        onToggle={() =>
                          setRelatedOpen((p) => ({ ...p, playlists: !p.playlists }))
                        }
                      >
                        <ArtistPlaylistsSection artistId={detail.entity_id} />
                      </RelatedSection>
                      <RelatedSection
                        id="sheet-related-collabs"
                        title="Collaborators"
                        open={relatedOpen.collabs}
                        onToggle={() =>
                          setRelatedOpen((p) => ({ ...p, collabs: !p.collabs }))
                        }
                      >
                        <CollaboratorsSection
                          artistId={detail.entity_id}
                          artistLabel={detail.title}
                        />
                      </RelatedSection>
                    </div>
                  )}
                </div>
              )}

              {tab === "notes" && (
                <div className="space-y-5">
                  {notesDefs.length === 0 ? (
                    <EmptyState
                      title="No notes fields yet"
                      description="Add a long-text field in Settings — it shows up here."
                      action={
                        <Button variant="secondary" size="sm" asChild>
                          <Link to="/settings">Open Settings</Link>
                        </Button>
                      }
                    />
                  ) : (
                    notesDefs.map((def) => (
                      <label key={def.id} className="block space-y-1">
                        <span className="text-xs text-muted">{def.label}</span>
                        <FieldEditor
                          def={def}
                          value={detail.values[def.key]}
                          disabled={busy}
                          onCommit={(next) => saveValues.mutate({ [def.key]: next })}
                        />
                      </label>
                    ))
                  )}
                </div>
              )}

              {tab === "meta" && (
                <MetaSection
                  entityType={detail.entity_type}
                  entityId={detail.entity_id}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Wrapper so the mutation hooks below always have a concrete target. */
export function EntitySheet({ onClose }: { onClose: () => void }) {
  const target = useDetailStore((s) => s.target);

  if (!target) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <EmptyState
          title="Nothing selected"
          description="Click a row, or press Enter in the grid, to see everything about it here."
        />
      </div>
    );
  }

  return <EntitySheetBody key={`${target.type}:${target.id}`} target={target} onClose={onClose} />;
}
